import { useState, useEffect } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useMiddlewareClient } from '@qwickforge/react-client';
import { Text } from '@qwickapps/react-framework';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ComputerIcon from '@mui/icons-material/Computer';

interface Device {
  id: string;
  name: string;
  hostname?: string;
  lastSeenAt?: string;
  deletedAt?: string;
}

interface DeviceStatusProps {
  deviceId: string | undefined;
}

export function getDeviceStatus(lastSeenAt: string | undefined): {
  color: 'success' | 'warning' | 'disabled' | 'error';
  label: string;
} {
  if (!lastSeenAt) {
    return { color: 'disabled', label: 'Never seen' };
  }

  const lastSeen = new Date(lastSeenAt);
  const now = new Date();
  const minutesAgo = (now.getTime() - lastSeen.getTime()) / (1000 * 60);

  // Helper function to format time in a human-readable way
  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${Math.floor(minutes)} min ago`;
    } else if (minutes < 1440) { // Less than 24 hours
      const hours = Math.floor(minutes / 60);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      const days = Math.floor(minutes / 1440);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
  };

  if (minutesAgo < 5) {
    return { color: 'success', label: `Online (last seen ${formatTime(minutesAgo)})` };
  } else if (minutesAgo < 10) {
    return { color: 'warning', label: `Recently active (last seen ${formatTime(minutesAgo)})` };
  } else {
    return { color: 'disabled', label: `Last seen ${formatTime(minutesAgo)}` };
  }
}

export default function DeviceStatus({ deviceId }: DeviceStatusProps) {
  const client = useMiddlewareClient();
  const [device, setDevice] = useState<Device | null>(null);

  useEffect(() => {
    if (deviceId && client) {
      loadDevice();
      // Refresh device status every 30 seconds
      const interval = setInterval(loadDevice, 30000);
      return () => clearInterval(interval);
    }
  }, [deviceId, client]);

  const loadDevice = async () => {
    if (!deviceId || !client) return;

    try {
      const devicesData: any = await client.devices.list();

      // Handle both array and object response formats
      const deviceList = Array.isArray(devicesData) ? devicesData : (devicesData.devices || []);

      // Find the specific device
      const foundDevice = deviceList.find((d: Device) => d.id === deviceId && !d.deletedAt);

      setDevice(foundDevice || null);
    } catch (error) {
      console.error('Failed to load device:', error);
    }
  };

  if (!deviceId || !device) {
    return null;
  }

  const status = getDeviceStatus(device.lastSeenAt);

  return (
    <Tooltip title={status.label} placement="bottom">
      <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: 'var(--theme-surface)', px: 1, py: 1.71, borderRadius: 1, border: '1px solid var(--theme-border-light)' }}>
        <ComputerIcon sx={{ fontSize: 18, color: 'var(--theme-text-secondary)' }} />
        <FiberManualRecordIcon
          sx={{ fontSize: 12 }}
          color={status.color}
        />
        <Text variant="body2" content={device.name || device.hostname || 'Unknown Device'} customColor="var(--theme-text-secondary)" />
      </Box>
    </Tooltip>
  );
}
